import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const AdminAttendanceCollectReport = ({ onNavigate, userInfo }) => {
    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [summary, setSummary] = useState({ totalSessions: 0, totalDepartments: 0, totalStudents: 0, totalPresent: 0 });

    // Filters
    const today = new Date().toISOString().split('T')[0];
    const [selectedDate, setSelectedDate] = useState(today);
    const [selectedDepartment, setSelectedDepartment] = useState('all');
    const [teachers, setTeachers] = useState([]);
    const [selectedTeacher, setSelectedTeacher] = useState('all');

    // Expanded teacher
    const [expandedTeacher, setExpandedTeacher] = useState(null);

    // Photo modal
    const [showPhotoModal, setShowPhotoModal] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);

    // Track which images have loaded
    const [imageLoadedMap, setImageLoadedMap] = useState({});
    const [modalImageLoaded, setModalImageLoaded] = useState(false);
    const [imagesReady, setImagesReady] = useState(true); // true when no images or all loaded

    // Fetch departments from backend (same as student/teacher management)
    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                if (!token) return;
                const response = await fetch(`${API_LINK}/api/admin/departments`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const result = await response.json();
                if (result.success) {
                    setDepartments(result.data || []);
                }
            } catch (error) {
                console.error('Error fetching departments:', error);
            }
        };
        fetchDepartments();
    }, []);

    // Fetch report data
    useEffect(() => {
        fetchReport();
    }, [selectedDate, selectedDepartment, selectedTeacher]);

    const fetchReport = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            if (!token) return;

            let url = `${API_LINK}/api/admin/attendance-collect-report?date=${selectedDate}`;
            if (selectedDepartment && selectedDepartment !== 'all') {
                url += `&department=${encodeURIComponent(selectedDepartment)}`;
            }
            if (selectedTeacher && selectedTeacher !== 'all') {
                url += `&teacher=${encodeURIComponent(selectedTeacher)}`;
            }

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const result = await response.json();
            if (result.success) {
                const records = result.data.records || [];
                setReportData(records);
                setTeachers(result.data.teachers || []);
                setSummary(result.data.summary || { totalSessions: 0, totalDepartments: 0, totalStudents: 0, totalPresent: 0 });
                setExpandedTeacher(null);
                setImageLoadedMap({});

                // Preload all classroom photos for PDF
                const photoPaths = records.filter(r => r.photo_path).map(r => r.photo_path);
                if (photoPaths.length > 0) {
                    setImagesReady(false);
                    const preloadPromises = photoPaths.map(p => {
                        return new Promise(resolve => {
                            const img = new Image();
                            img.onload = resolve;
                            img.onerror = resolve; // resolve even on error so we don't block forever
                            img.src = `${API_LINK}${p}`;
                        });
                    });
                    Promise.all(preloadPromises).then(() => setImagesReady(true));
                } else {
                    setImagesReady(true);
                }
            }
        } catch (error) {
            console.error('Error fetching report:', error);
        } finally {
            setLoading(false);
        }
    };

    // Map year values to display labels
    const getYearLabel = (yearVal) => {
        const y = parseInt(yearVal);
        if (y === 2001) return '1st Year';
        if (y === 2002) return '2nd Year';
        if (y === 2003) return '3rd Year';
        if (y === 2004) return '4th Year';
        return `Year ${yearVal}`;
    };

    const yearOrder = [2001, 2002, 2003, 2004];

    // Group report data by teacher (flat list for summary/PDF)
    const getTeacherGroups = () => {
        const groups = {};
        reportData.forEach(row => {
            const key = row.teacher_name || (row.teacher_id ? `Teacher #${row.teacher_id}` : `${row.department} - Unassigned`);
            if (!groups[key]) {
                groups[key] = {
                    teacher_name: row.teacher_name || (row.teacher_id ? `Teacher #${row.teacher_id}` : row.department || 'Unassigned'),
                    teacher_id: row.teacher_id,
                    department: row.department,
                    subjects: [],
                    totalStudents: 0,
                    totalPresent: 0
                };
            }
            groups[key].subjects.push(row);
            groups[key].totalStudents += parseInt(row.total_students) || 0;
            groups[key].totalPresent += parseInt(row.present_count) || 0;
        });
        return Object.values(groups);
    };

    // Group report data by year -> then by teacher within each year
    const getYearGroups = () => {
        const yearMap = {};
        reportData.forEach(row => {
            const year = parseInt(row.year) || 0;
            if (!yearMap[year]) {
                yearMap[year] = {};
            }
            const teacherKey = row.teacher_name || (row.teacher_id ? `Teacher #${row.teacher_id}` : `${row.department} - Unassigned`);
            if (!yearMap[year][teacherKey]) {
                yearMap[year][teacherKey] = {
                    teacher_name: row.teacher_name || (row.teacher_id ? `Teacher #${row.teacher_id}` : row.department || 'Unassigned'),
                    teacher_id: row.teacher_id,
                    department: row.department,
                    subjects: [],
                    totalStudents: 0,
                    totalPresent: 0
                };
            }
            yearMap[year][teacherKey].subjects.push(row);
            yearMap[year][teacherKey].totalStudents += parseInt(row.total_students) || 0;
            yearMap[year][teacherKey].totalPresent += parseInt(row.present_count) || 0;
        });

        // Sort by year order
        const sortedYears = Object.keys(yearMap)
            .map(Number)
            .sort((a, b) => {
                const aIdx = yearOrder.indexOf(a);
                const bIdx = yearOrder.indexOf(b);
                if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
                if (aIdx !== -1) return -1;
                if (bIdx !== -1) return 1;
                return a - b;
            });

        return sortedYears.map(year => ({
            year,
            label: getYearLabel(year),
            teachers: Object.values(yearMap[year]),
            totalStudents: Object.values(yearMap[year]).reduce((s, t) => s + t.totalStudents, 0),
            totalPresent: Object.values(yearMap[year]).reduce((s, t) => s + t.totalPresent, 0)
        }));
    };

    const teacherGroups = getTeacherGroups();
    const yearGroups = getYearGroups();

    // ─── PDF helpers ───────────────────────────────────────────────
    const loadImageAsBase64 = (url) => {
        return new Promise((resolve) => {
            const timeout = setTimeout(() => resolve(null), 5000);
            fetch(url, { mode: 'cors' })
                .then(res => {
                    if (!res.ok) { clearTimeout(timeout); return resolve(null); }
                    return res.blob();
                })
                .then(blob => {
                    if (!blob) return;
                    const reader = new FileReader();
                    reader.onloadend = () => { clearTimeout(timeout); resolve(reader.result); };
                    reader.onerror = () => { clearTimeout(timeout); resolve(null); };
                    reader.readAsDataURL(blob);
                })
                .catch(() => { clearTimeout(timeout); resolve(null); });
        });
    };

    const generatePDF = async () => {
        if (reportData.length === 0) return;
        setPdfLoading(true);

        try {
            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            let y = 15;

            // ── Header ──────────────────────────────────────────
            doc.setFillColor(30, 81, 40);
            doc.rect(0, 0, pageWidth, 28, 'F');

            doc.setTextColor(255, 255, 255);
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.text('Swami Vivekananda University', pageWidth / 2, 11, { align: 'center' });

            doc.setFontSize(12);
            doc.setFont('helvetica', 'normal');
            doc.text('Attendance Collect Report', pageWidth / 2, 18, { align: 'center' });

            doc.setFontSize(9);
            doc.text(`Date: ${selectedDate}`, pageWidth / 2, 24, { align: 'center' });

            y = 34;

            // ── Summary line ────────────────────────────────────
            doc.setTextColor(60, 60, 60);
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            const summaryText = `Total Sessions: ${summary.totalSessions}  |  Teachers: ${teacherGroups.length}  |  Total Students: ${summary.totalStudents}  |  Total Present: ${summary.totalPresent}`;
            doc.text(summaryText, pageWidth / 2, y, { align: 'center' });
            y += 8;

            // ── Group by department ──────────────────────────────
            const deptGroups = {};
            reportData.forEach(row => {
                const dept = row.department || 'Unknown Department';
                if (!deptGroups[dept]) deptGroups[dept] = [];
                deptGroups[dept].push(row);
            });

            // ── Pre-load all photos ─────────────────────────────
            const photoCache = {};
            const photoPromises = reportData
                .filter(r => r.photo_path)
                .map(async (r) => {
                    const url = `${API_LINK}${r.photo_path}`;
                    if (!photoCache[url]) {
                        photoCache[url] = await loadImageAsBase64(url);
                    }
                });
            await Promise.all(photoPromises);

            // ── Render each department ──────────────────────────
            const deptNames = Object.keys(deptGroups).sort();

            for (let dIdx = 0; dIdx < deptNames.length; dIdx++) {
                const deptName = deptNames[dIdx];
                const rows = deptGroups[deptName];

                // New page if not enough space
                if (y > pageHeight - 40) {
                    doc.addPage();
                    y = 15;
                }

                // Department heading
                doc.setFillColor(232, 245, 233);
                doc.roundedRect(10, y - 5, pageWidth - 20, 9, 2, 2, 'F');
                doc.setTextColor(30, 81, 40);
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.text(`Department: ${deptName}`, 14, y + 1);
                y += 10;

                // Build table rows
                const tableBody = [];
                const rowImages = [];

                rows.forEach((row, idx) => {
                    const students = parseInt(row.total_students) || 0;
                    const present = parseInt(row.present_count) || 0;
                    const pct = students > 0 ? Math.round((present / students) * 100) : 0;
                    tableBody.push([
                        idx + 1,
                        row.teacher_name || row.department || 'Unassigned',
                        row.subject || '-',
                        `${row.batch_name || '-'} / Year ${row.year || '-'}`,
                        students,
                        present,
                        `${pct}%`,
                        '' // placeholder for photo
                    ]);

                    const photoUrl = row.photo_path ? `${API_LINK}${row.photo_path}` : null;
                    rowImages.push(photoUrl ? (photoCache[photoUrl] || null) : null);
                });

                // Draw table
                autoTable(doc, {
                    startY: y,
                    head: [['#', 'Teacher Name', 'Subject', 'Batch / Year', 'Total Students', 'Present', 'Attendance %', 'Photo']],
                    body: tableBody,
                    theme: 'grid',
                    margin: { left: 10, right: 10 },
                    styles: {
                        fontSize: 8,
                        cellPadding: 3,
                        valign: 'middle',
                        overflow: 'linebreak'
                    },
                    headStyles: {
                        fillColor: [30, 81, 40],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold',
                        halign: 'center',
                        fontSize: 8.5
                    },
                    columnStyles: {
                        0: { halign: 'center', cellWidth: 10 },
                        1: { cellWidth: 45 },
                        2: { cellWidth: 40 },
                        3: { cellWidth: 35 },
                        4: { halign: 'center', cellWidth: 28 },
                        5: { halign: 'center', cellWidth: 22 },
                        6: { halign: 'center', cellWidth: 28 },
                        7: { halign: 'center', cellWidth: 30 }
                    },
                    didDrawCell: function (data) {
                        // Embed photo in the last column of body rows
                        if (data.column.index === 7 && data.section === 'body') {
                            const imgData = rowImages[data.row.index];
                            if (imgData) {
                                const dim = 16;
                                const xPos = data.cell.x + (data.cell.width - dim) / 2;
                                const yPos = data.cell.y + (data.cell.height - dim) / 2;
                                try {
                                    doc.addImage(imgData, 'JPEG', xPos, yPos, dim, dim);
                                } catch (e) {
                                    // Skip if image fails
                                }
                            }
                        }
                    },
                    // Ensure rows with photos have enough height
                    didParseCell: function (data) {
                        if (data.column.index === 7 && data.section === 'body') {
                            const imgData = rowImages[data.row.index];
                            if (imgData) {
                                data.cell.styles.minCellHeight = 20;
                            }
                        }
                    }
                });

                y = doc.lastAutoTable.finalY + 10;
            }

            // ── Footer on every page ────────────────────────────
            const totalPages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFontSize(7);
                doc.setTextColor(150);
                doc.text(
                    `Generated on ${new Date().toLocaleString()} | Page ${i} of ${totalPages}`,
                    pageWidth / 2, pageHeight - 5, { align: 'center' }
                );
            }

            // ── Save ────────────────────────────────────────────
            const deptSuffix = selectedDepartment !== 'all' ? `_${selectedDepartment}` : '';
            doc.save(`Attendance_Report_${selectedDate}${deptSuffix}.pdf`);
        } catch (err) {
            console.error('PDF generation error:', err);
            console.error('Stack:', err.stack);
            alert('PDF Error: ' + (err.message || err));
        } finally {
            setPdfLoading(false);
        }
    };

    const getAttendanceColor = (percentage) => {
        if (percentage >= 90) return '#238548';
        if (percentage >= 75) return '#2196F3';
        if (percentage >= 50) return '#FF9800';
        return '#f44336';
    };

    // Styles
    const labelStyle = {
        display: 'block',
        marginBottom: '0.4rem',
        fontWeight: '600',
        color: '#555',
        fontSize: '0.85rem'
    };

    const inputStyle = {
        width: '100%',
        padding: '0.7rem 1rem',
        border: '2px solid #e0e0e0',
        borderRadius: '8px',
        fontSize: '0.95rem',
        outline: 'none',
        boxSizing: 'border-box'
    };

    const cardStyle = {
        background: '#fff',
        borderRadius: '12px',
        padding: '1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        flex: '1',
        minWidth: '200px'
    };

    return (
        <Layout role="admin" onNavigate={onNavigate} activePage="admin-attendance-collect-report" userInfo={userInfo}>
            {/* Page Header */}
            <div style={{ marginBottom: '1.5rem' }}>
                <h1 style={{ margin: 0, fontSize: '1.8rem', color: '#1a1a2e', fontWeight: '700' }}>
                    📊 Attendance Collect Report
                </h1>
                <p style={{ margin: '0.3rem 0 0', color: '#666', fontSize: '0.95rem' }}>
                    Department-wise attendance collection — click a teacher to view subject details
                </p>
            </div>

            {/* Filters */}
            <div style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '1.2rem 1.5rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                marginBottom: '1.5rem',
                display: 'flex',
                gap: '1.5rem',
                flexWrap: 'wrap',
                alignItems: 'flex-end'
            }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                    <label style={labelStyle}>📅 Select Date</label>
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        style={inputStyle}
                    />
                </div>
                <div style={{ flex: '1', minWidth: '200px' }}>
                    <label style={labelStyle}>🏛️ Department</label>
                    <select
                        value={selectedDepartment}
                        onChange={(e) => setSelectedDepartment(e.target.value)}
                        style={inputStyle}
                    >
                        <option value="all">All Departments</option>
                        {departments.map((dept, i) => (
                            <option key={dept.value || i} value={dept.value}>{dept.label}</option>
                        ))}
                    </select>
                </div>
                <div style={{ flex: '1', minWidth: '200px' }}>
                    <label style={labelStyle}>👨‍🏫 Teacher</label>
                    <select
                        value={selectedTeacher}
                        onChange={(e) => setSelectedTeacher(e.target.value)}
                        style={inputStyle}
                    >
                        <option value="all">All Teachers</option>
                        {teachers.map((teacher) => (
                            <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
                        ))}
                    </select>
                </div>
                <div style={{ display: 'flex', gap: '0.8rem' }}>
                    <button
                        onClick={fetchReport}
                        style={{
                            padding: '0.7rem 1.5rem',
                            background: '#238548',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.95rem'
                        }}
                    >
                        🔄 Refresh
                    </button>
                    <button
                        onClick={generatePDF}
                        disabled={pdfLoading || reportData.length === 0 || !imagesReady}
                        style={{
                            padding: '0.7rem 1.5rem',
                            background: (reportData.length === 0 || !imagesReady) ? '#ccc' : '#d32f2f',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: (reportData.length === 0 || !imagesReady) ? 'not-allowed' : 'pointer',
                            fontWeight: '600',
                            fontSize: '0.95rem',
                            opacity: (pdfLoading || !imagesReady) ? 0.7 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem'
                        }}
                    >
                        {pdfLoading ? '⏳ Generating...' : !imagesReady ? '⏳ Loading Images...' : '📥 Download PDF'}
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                <div style={{ ...cardStyle, borderLeft: '4px solid #238548' }}>
                    <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.3rem' }}>Total Sessions</div>
                    <div style={{ fontSize: '2rem', fontWeight: '700', color: '#238548' }}>{summary.totalSessions}</div>
                </div>
                <div style={{ ...cardStyle, borderLeft: '4px solid #2196F3' }}>
                    <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.3rem' }}>Teachers</div>
                    <div style={{ fontSize: '2rem', fontWeight: '700', color: '#2196F3' }}>{teacherGroups.length}</div>
                </div>
                <div style={{ ...cardStyle, borderLeft: '4px solid #FF9800' }}>
                    <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.3rem' }}>Total Students</div>
                    <div style={{ fontSize: '2rem', fontWeight: '700', color: '#FF9800' }}>{summary.totalStudents}</div>
                </div>
                <div style={{ ...cardStyle, borderLeft: '4px solid #9C27B0' }}>
                    <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.3rem' }}>Total Present</div>
                    <div style={{ fontSize: '2rem', fontWeight: '700', color: '#9C27B0' }}>{summary.totalPresent}</div>
                </div>
            </div>

            {/* Year-wise Attendance List */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
                    <div style={{
                        width: '40px', height: '40px', border: '4px solid #e0e0e0', borderTop: '4px solid #238548',
                        borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem'
                    }} />
                    Loading report...
                </div>
            ) : yearGroups.length === 0 ? (
                <div style={{
                    textAlign: 'center', padding: '3rem', color: '#888',
                    background: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                    <h3 style={{ margin: 0, color: '#555' }}>No Attendance Records Found</h3>
                    <p style={{ margin: '0.5rem 0 0' }}>No attendance sessions found for {selectedDate}.</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    {yearGroups.map((yearGroup) => {
                        const yearPct = yearGroup.totalStudents > 0
                            ? Math.round((yearGroup.totalPresent / yearGroup.totalStudents) * 100)
                            : 0;

                        return (
                            <div key={yearGroup.year}>
                                {/* Year Section Header */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    background: 'linear-gradient(135deg, #238548 0%, #2ea960 100%)',
                                    color: '#fff',
                                    padding: '1rem 1.5rem',
                                    borderRadius: '12px 12px 0 0',
                                    flexWrap: 'wrap',
                                    gap: '0.5rem'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                                        <span style={{ fontSize: '1.5rem' }}>🎓</span>
                                        <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: '700' }}>
                                            {yearGroup.label}
                                        </h2>
                                    </div>
                                </div>

                                {/* Teachers within this year */}
                                <div style={{
                                    background: '#fff',
                                    borderRadius: '0 0 12px 12px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    overflow: 'hidden'
                                }}>
                                    {yearGroup.teachers.map((teacher, tIdx) => {
                                        const expandKey = `${yearGroup.year}-${tIdx}`;
                                        const isExpanded = expandedTeacher === expandKey;
                                        const overallPct = teacher.totalStudents > 0
                                            ? Math.round((teacher.totalPresent / teacher.totalStudents) * 100)
                                            : 0;

                                        return (
                                            <div key={tIdx} style={{
                                                borderBottom: tIdx < yearGroup.teachers.length - 1 ? '1px solid #eee' : 'none'
                                            }}>
                                                {/* Teacher Header — clickable */}
                                                <div
                                                    onClick={() => setExpandedTeacher(isExpanded ? null : expandKey)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        padding: '1rem 1.5rem',
                                                        cursor: 'pointer',
                                                        background: isExpanded ? '#f0faf3' : '#fff',
                                                        transition: 'background 0.2s ease',
                                                        gap: '1rem',
                                                        flexWrap: 'wrap'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: '1', minWidth: '200px' }}>
                                                        {/* Avatar */}
                                                        <div style={{
                                                            width: '42px', height: '42px', borderRadius: '50%',
                                                            background: '#238548', color: '#fff',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            fontSize: '1.1rem', fontWeight: '700', flexShrink: 0
                                                        }}>
                                                            {(teacher.teacher_name || 'T')[0].toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div style={{ fontWeight: '600', fontSize: '1rem', color: '#1a1a2e' }}>
                                                                {teacher.teacher_name}
                                                            </div>
                                                            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '2px' }}>
                                                                {teacher.subjects.length} subject{teacher.subjects.length > 1 ? 's' : ''}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Summary pills */}
                                                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{ fontSize: '0.7rem', color: '#888' }}>Students</div>
                                                            <div style={{ fontWeight: '700', fontSize: '1rem', color: '#333' }}>{teacher.totalStudents}</div>
                                                        </div>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{ fontSize: '0.7rem', color: '#888' }}>Present</div>
                                                            <div style={{ fontWeight: '700', fontSize: '1rem', color: '#238548' }}>{teacher.totalPresent}</div>
                                                        </div>
                                                        <div style={{
                                                            padding: '0.25rem 0.7rem',
                                                            borderRadius: '20px',
                                                            background: getAttendanceColor(overallPct) + '18',
                                                            color: getAttendanceColor(overallPct),
                                                            fontWeight: '700',
                                                            fontSize: '0.85rem'
                                                        }}>
                                                            {overallPct}%
                                                        </div>
                                                        <div style={{
                                                            fontSize: '1rem',
                                                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                            transition: 'transform 0.3s ease',
                                                            color: '#888'
                                                        }}>
                                                            ▼
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Expanded Subject Details */}
                                                {isExpanded && (
                                                    <div style={{
                                                        borderTop: '1px solid #e8e8e8',
                                                        padding: '0'
                                                    }}>
                                                        {teacher.subjects.map((sub, sIdx) => {
                                                            const subPct = sub.total_students > 0
                                                                ? Math.round((sub.present_count / sub.total_students) * 100)
                                                                : 0;
                                                            const imgKey = `${yearGroup.year}-${tIdx}-${sIdx}`;

                                                            return (
                                                                <div key={sIdx} style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    padding: '0.8rem 1.5rem 0.8rem 5rem',
                                                                    borderBottom: sIdx < teacher.subjects.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                                    background: sIdx % 2 === 0 ? '#fafffe' : '#fff',
                                                                    gap: '1rem',
                                                                    flexWrap: 'wrap'
                                                                }}>
                                                                    {/* Subject & Batch info */}
                                                                    <div style={{ flex: '1', minWidth: '180px' }}>
                                                                        <div style={{ fontWeight: '600', color: '#333', fontSize: '0.95rem' }}>
                                                                            📚 {sub.subject}
                                                                        </div>
                                                                        <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '3px' }}>
                                                                            Batch: {sub.batch_name}
                                                                        </div>
                                                                    </div>

                                                                    {/* Counts */}
                                                                    <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                                                        <div style={{ textAlign: 'center' }}>
                                                                            <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>In Class</div>
                                                                            <div style={{ fontWeight: '700', fontSize: '1.1rem', color: '#333' }}>{sub.total_students}</div>
                                                                        </div>
                                                                        <div style={{ textAlign: 'center' }}>
                                                                            <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>Present</div>
                                                                            <div style={{ fontWeight: '700', fontSize: '1.1rem', color: '#238548' }}>{sub.present_count}</div>
                                                                        </div>
                                                                        <div style={{
                                                                            padding: '0.25rem 0.7rem',
                                                                            borderRadius: '16px',
                                                                            background: getAttendanceColor(subPct) + '18',
                                                                            color: getAttendanceColor(subPct),
                                                                            fontWeight: '700',
                                                                            fontSize: '0.85rem',
                                                                            minWidth: '50px',
                                                                            textAlign: 'center'
                                                                        }}>
                                                                            {subPct}%
                                                                        </div>

                                                                        {/* Photo thumbnail */}
                                                                        {sub.photo_path ? (
                                                                            <div style={{ position: 'relative', width: '52px', height: '52px', flexShrink: 0 }}>
                                                                                {/* Loading shimmer placeholder */}
                                                                                {!imageLoadedMap[imgKey] && (
                                                                                    <div style={{
                                                                                        position: 'absolute', top: 0, left: 0,
                                                                                        width: '52px', height: '52px', borderRadius: '8px',
                                                                                        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                                                                                        backgroundSize: '200% 100%',
                                                                                        animation: 'shimmer 1.5s ease-in-out infinite',
                                                                                        border: '2px solid #e0e0e0',
                                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                                                    }}>
                                                                                        <div style={{
                                                                                            width: '18px', height: '18px',
                                                                                            border: '2px solid #ccc', borderTop: '2px solid #238548',
                                                                                            borderRadius: '50%', animation: 'spin 1s linear infinite'
                                                                                        }} />
                                                                                    </div>
                                                                                )}
                                                                                <img
                                                                                    src={`${API_LINK}${sub.photo_path}`}
                                                                                    alt="Class"
                                                                                    onClick={() => {
                                                                                        setSelectedPhoto(`${API_LINK}${sub.photo_path}`);
                                                                                        setModalImageLoaded(false);
                                                                                        setShowPhotoModal(true);
                                                                                    }}
                                                                                    style={{
                                                                                        width: '52px', height: '52px', borderRadius: '8px',
                                                                                        objectFit: 'cover', cursor: 'pointer',
                                                                                        border: '2px solid #e0e0e0', transition: 'transform 0.2s',
                                                                                        opacity: imageLoadedMap[imgKey] ? 1 : 0
                                                                                    }}
                                                                                    onLoad={() => setImageLoadedMap(prev => ({ ...prev, [imgKey]: true }))}
                                                                                    onMouseOver={(e) => e.target.style.transform = 'scale(1.15)'}
                                                                                    onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                                                                                    onError={(e) => { e.target.style.display = 'none'; setImageLoadedMap(prev => ({ ...prev, [imgKey]: true })); }}
                                                                                />
                                                                            </div>
                                                                        ) : (
                                                                            <div style={{
                                                                                width: '52px', height: '52px', borderRadius: '8px',
                                                                                background: '#f5f5f5', border: '2px dashed #ddd',
                                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                                fontSize: '0.65rem', color: '#bbb', textAlign: 'center'
                                                                            }}>
                                                                                No Photo
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Photo Modal */}
            {showPhotoModal && selectedPhoto && (
                <div
                    onClick={() => setShowPhotoModal(false)}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.8)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        zIndex: 9999, cursor: 'pointer'
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'relative', background: '#fff',
                            borderRadius: '12px', padding: '1rem', maxWidth: '90vw', maxHeight: '90vh',
                            minWidth: '200px', minHeight: '200px'
                        }}
                    >
                        <button
                            onClick={() => setShowPhotoModal(false)}
                            style={{
                                position: 'absolute', top: '-12px', right: '-12px',
                                width: '32px', height: '32px', borderRadius: '50%',
                                background: '#f44336', color: '#fff', border: 'none',
                                cursor: 'pointer', fontSize: '1.1rem', fontWeight: '700',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                zIndex: 1
                            }}
                        >
                            ✕
                        </button>
                        {/* Loading spinner for modal image */}
                        {!modalImageLoaded && (
                            <div style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                justifyContent: 'center', padding: '3rem', gap: '1rem'
                            }}>
                                <div style={{
                                    width: '40px', height: '40px',
                                    border: '4px solid #e0e0e0', borderTop: '4px solid #238548',
                                    borderRadius: '50%', animation: 'spin 1s linear infinite'
                                }} />
                                <span style={{ color: '#888', fontSize: '0.9rem' }}>Loading image...</span>
                            </div>
                        )}
                        <img
                            src={selectedPhoto}
                            alt="Classroom Photo"
                            onLoad={() => setModalImageLoaded(true)}
                            style={{
                                maxWidth: '85vw', maxHeight: '80vh',
                                borderRadius: '8px', objectFit: 'contain',
                                display: modalImageLoaded ? 'block' : 'none'
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Shimmer & spinner keyframes */}
            <style>{`
                @keyframes shimmer {
                    0% { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </Layout>
    );
};

export default AdminAttendanceCollectReport;
